import { getUserMediaAudio, getCurrentInputDeviceId, setCurrentInputDeviceId } from "../utils/audioDevices";
import { ListenerRegistry } from "../utils/listenerRegistry";

export enum KeyType {
  SPACE = -1,
  Z = 0,
  X = 1,
  C = 2,
  V = 3,
}

export const MIC_DEACTIVATION_DELAY_MS = 150;

export type WaveformCallback = (
  data: Int16Array,
  isFirst: boolean,
  isLast: boolean,
  keyType: KeyType
) => void;

export class MicrophoneImpl {
  private static instance: MicrophoneImpl | null = null;

  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  // Analyser and adjustGainLoopId are no longer needed for fixed gain

  private onWaveformCallbacks = new ListenerRegistry<WaveformCallback>();
  private onWaveformTaps = new ListenerRegistry<WaveformCallback>();

  private isFirst = true;
  public shouldCallCallback = false;
  private shouldShutDownCallback = false;
  private activeKeyType: KeyType = KeyType.SPACE;

  private readonly MAX_BUFFER_SIZE = 8000;
  private audioBuffer: Int16Array[] = [];
  private bufferSampleCount = 0;

  private workletNode: AudioWorkletNode | null = null;
  private trackEndedHandler: (() => void) | null = null;

  started: boolean = false;
  selectedMicrophoneId: string | undefined = undefined;

  private constructor() {
    console.log("[MicrophoneImpl] constructing, selectedMicrophoneId:", this.selectedMicrophoneId);
  }

  public static getInstance(): MicrophoneImpl {
    if (!MicrophoneImpl.instance) {
      MicrophoneImpl.instance = new MicrophoneImpl();
    }
    return MicrophoneImpl.instance;
  }

  addWaveformCallback(key: string, callback: WaveformCallback) {
    this.onWaveformCallbacks.add(key, callback);
  }

  removeWaveformCallback(key: string) {
    this.onWaveformCallbacks.remove(key);
  }

  addWaveformTap(key: string, callback: WaveformCallback) {
    this.onWaveformTaps.add(key, callback);
  }

  removeWaveformTap(key: string) {
    this.onWaveformTaps.remove(key);
  }

  isStarted(): boolean {
    return this.started;
  }

  private async initAndWireContext(
    context: AudioContext,
    stream: MediaStream,
    processorOptions?: Record<string, unknown>
  ): Promise<void> {
    console.log("[MicrophoneImpl] Real sample rate:", context.sampleRate);

    await Promise.all([
      context.resume(),
      context.audioWorklet.addModule("./microphone-processor.js"),
    ]);

    // This can throw on Firefox when rates mismatch (probe attach)
    const source = new MediaStreamAudioSourceNode(context, { mediaStream: stream });

    const workletNode = new AudioWorkletNode(
      context,
      "microphone-processor",
      processorOptions ? { processorOptions } : undefined
    );

    const nodeId = Math.random();
    console.log("[MicrophoneImpl] Created workletNode", nodeId);

    workletNode.port.onmessage = (event) => {
      const currentData = event.data as Int16Array;

      if (this.shouldCallCallback) {
        if (this.bufferSampleCount > 0) {
          const totalSize = this.bufferSampleCount + currentData.length;
          const combinedData = new Int16Array(totalSize);
          let offset = 0;
          for (const bufferChunk of this.audioBuffer) {
            combinedData.set(bufferChunk, offset);
            offset += bufferChunk.length;
          }
          combinedData.set(currentData, offset);
          this.audioBuffer = [];
          this.bufferSampleCount = 0;

          this.onWaveformCallbacks.notify(
            combinedData,
            this.isFirst,
            this.shouldShutDownCallback,
            this.activeKeyType
          );
        } else {
          this.onWaveformCallbacks.notify(
            currentData,
            this.isFirst,
            this.shouldShutDownCallback,
            this.activeKeyType
          );
        }
        this.isFirst = false;
        if (this.shouldShutDownCallback) this.shouldCallCallback = false;
      } else {
        this.addToBuffer(currentData);
      }

      this.onWaveformTaps.notify(
        currentData,
        this.isFirst,
        this.shouldShutDownCallback,
        this.activeKeyType
      );
    };

    source.connect(workletNode);

    this.mediaStream = stream;
    this.audioContext = context;
    this.workletNode = workletNode;

    // Add track ended listener for automatic device recovery
    this.setupTrackEndedListener(stream);
  }

  private removeTrackEndedHandler() {
    if (this.trackEndedHandler && this.mediaStream) {
      const handler = this.trackEndedHandler;
      this.mediaStream.getAudioTracks().forEach((track) => {
        track.removeEventListener("ended", handler);
      });
    }
    this.trackEndedHandler = null;
  }

  private setupTrackEndedListener(stream: MediaStream): void {
    // Remove any existing handler
    this.removeTrackEndedHandler();

    // Create new handler
    this.trackEndedHandler = () => {
      console.warn("[MicrophoneImpl] Audio track ended (device likely unplugged). Attempting automatic recovery...");
      this.handleDeviceDisconnection();
    };

    // Add listener to all audio tracks
    const tracks = stream.getAudioTracks();
    const handler = this.trackEndedHandler;
    tracks.forEach((track) => {
      track.addEventListener("ended", handler);
    });
  }

  private async handleDeviceDisconnection(): Promise<void> {
    console.log("[MicrophoneImpl] handleDeviceDisconnection called");

    // Save the current state
    const wasCallbackEnabled = this.shouldCallCallback;
    const currentKeyType = this.activeKeyType;

    // Clean up current stream
    this.shutDown();
    this.started = false;

    // Don't clear selectedMicrophoneId here - let start()'s error handling do it
    // This avoids race conditions and keeps state management in one place

    // If the mic was active, try to restart
    // start() will attempt to use the stored device and handle OverconstrainedError if it fails
    if (wasCallbackEnabled) {
      try {
        await this.start();
        // Re-enable callback if it was enabled before
        this.setCallbackEnabled(true, currentKeyType);
        console.log("[MicrophoneImpl] Successfully recovered to default device");
      } catch (err) {
        console.error("[MicrophoneImpl] Failed to recover microphone:", err);
      }
    }
  }

  private async tryAttach16k(stream: MediaStream): Promise<boolean> {
    let context: AudioContext | null = null;
    try {
      context = new AudioContext({ sampleRate: 16000 });
      await this.initAndWireContext(context, stream); // no processorOptions → exact original behavior
      console.log("[MicrophoneImpl] Starting microphone (16k path).");
      return true;
    } catch (err) {
      console.warn("[MicrophoneImpl] 16k path failed; will fall back:", err);
      try { await context?.close(); } catch { }
      return false;
    }
  }

  async start(): Promise<void> {
    try {
      if (this.started) return;
      this.started = true;

      // If no device selected yet, try to use stored preference
      if (!this.selectedMicrophoneId) {
        const storedDeviceId = getCurrentInputDeviceId();
        if (storedDeviceId) {
          this.selectedMicrophoneId = storedDeviceId;
          console.log("[MicrophoneImpl] Using stored microphone device:", storedDeviceId);
        }
      }

      let stream: MediaStream;
      try {
        stream = await getUserMediaAudio(this.selectedMicrophoneId);
      } catch (error: any) {
        if (error.name === 'OverconstrainedError') {
          console.warn('[MicrophoneImpl] Stored device unavailable (likely unplugged), falling back to default');
          // Clear the invalid device ID
          this.selectedMicrophoneId = undefined;
          setCurrentInputDeviceId(null);
          // Retry without device constraint
          stream = await getUserMediaAudio(undefined);
        } else {
          throw error;
        }
      }

      // Log which device is actually being used
      const track = stream.getAudioTracks()[0];
      if (track) {
        const settings = track.getSettings();
        console.log("[MicrophoneImpl] Microphone device info:", {
          requestedDeviceId: this.selectedMicrophoneId || "(none - using browser default)",
          actualDeviceId: settings.deviceId || "(unknown)",
          deviceLabel: track.label || "(unknown)",
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
        });
      }

      // Attempt 16k graph first; fallback enables downsampling in the processor
      const ok = await this.tryAttach16k(stream);
      if (ok) return;

      const fbContext = new AudioContext();
      await this.initAndWireContext(fbContext, stream, { downsampleToHz: 16000 });
      console.log("[MicrophoneImpl] Starting microphone (fallback with downsampling).");
    } catch (err) {
      console.error("[MicrophoneImpl] Error accessing microphone:", err);
      throw new Error("Could not access microphone");
    }
  }

  stop() {
    console.log("[MicrophoneImpl] stop");
    this.shutDown();
    this.started = false;
  }

  setCallbackEnabled(enabled: boolean, keyType: KeyType = KeyType.SPACE) {
    console.log("[MicrophoneImpl] setCallbackEnabled: ", enabled, "keyType:", keyType);
    if (enabled) {
      this.isFirst = true;
      this.shouldShutDownCallback = false;
      this.shouldCallCallback = true;
      this.activeKeyType = keyType;
    } else {
      this.shouldShutDownCallback = true;
    }
  }

  private addToBuffer(data: Int16Array): void {
    this.audioBuffer.push(data);
    this.bufferSampleCount += data.length;
    while (this.bufferSampleCount > this.MAX_BUFFER_SIZE) {
      const oldestChunk = this.audioBuffer.shift();
      if (oldestChunk) {
        this.bufferSampleCount -= oldestChunk.length;
      }
    }
  }

  private shutDown() {
    console.log("[MicrophoneImpl] shutDown");

    // Remove track ended listeners before stopping tracks
    this.removeTrackEndedHandler();

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    // No adjustGainLoopId to clear
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    if (this.workletNode) {
      this.workletNode.port.onmessage = null; // remove message listener
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    // No analyser to disconnect
    console.log("[MicrophoneImpl] deactivated for real");
  }
}
